import styled, { StyledComponent } from "@emotion/styled";
import { callAllHandlers } from "@smartsheet/lodestar-utils";
import React, {
    forwardRef,
    ForwardRefExoticComponent,
    HTMLAttributes,
    RefObject,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { TabList as BaseTabList } from "reakit/Tab";
import { buttonPrimary, neutralAccessible, neutralLight20 } from "../../constants/colors";
import { sizes } from "../../constants/spacing";
import { LodestarCoreTheme, themed } from "../../util/theme";
import { TabUnderlineProps, TabUnderline as DefaultTabUnderline } from "./TabUnderline";
import { TabsContext } from "./TabsContext";
import { findIndex } from "./findIndex";

const TabsContainer = styled(BaseTabList)`
    display: inline-flex;
    height: ${sizes.large}px;
    // Adding relative to allow absolutely-positioned Underlines.
    // This also creates a stacking context for the tabs/underline without using a z-index.
    position: relative;
    isolation: isolate;
    white-space: nowrap;
`;

const useUnderline = (
    tabRef: RefObject<HTMLElement>
): {
    left: number;
    width: number;
} => {
    const [sliderCoords, setSliderCoords] = useState({
        left: 0,
        width: 0,
    });

    useEffect(() => {
        if (tabRef && tabRef.current) {
            const left = tabRef.current.offsetLeft;
            const width = tabRef.current.offsetWidth;
            setSliderCoords({ left, width });
        }
    }, [tabRef]);

    return sliderCoords;
};

const underlineTheme = themed({
    selection: {
        "lodestar-light": buttonPrimary,
        oldestar: "#0073EC",
        "ursa-light": buttonPrimary,
    },
});

export interface TabListProps extends HTMLAttributes<HTMLElement> {
    /**
     * Overrides allowing customization of components internal to this component.
     */
    components?: {
        TabUnderline?:
            | ForwardRefExoticComponent<TabUnderlineProps>
            // eslint-disable-next-line @typescript-eslint/ban-types
            | StyledComponent<TabUnderlineProps, object, object>;
    };
}

export const TabList = forwardRef<HTMLDivElement, TabListProps>(({ children, components = {}, ...rest }, ref) => {
    const underlineShouldTransitionRef = useRef(false);
    const showTransitions = () => {
        // We only want to start transitioning on the 1st hover or selection.
        // We don't want to transtition on mount or the bar grows from 0
        // to whatever the selected item's width is.
        // Use a ref since changing this shouldn't cause a re-render by itself,
        // but it will be used in subsequent renders.
        underlineShouldTransitionRef.current = true;
    };

    const themeMode = React.useContext(LodestarCoreTheme);
    const { onChange: _0, onlyRenderActiveTabPanelChildren: _1, ...tab } = React.useContext(TabsContext);
    const tabRefs = useMemo(() => tab.items.map((i) => i.ref), [tab.items]);

    const selectedIndex = findIndex(tab.items, (i) => i.id === tab.selectedId);
    const selectedSliderCoords = useUnderline(tabRefs[selectedIndex]);

    const [hoveredSliderCoords, setHoveredSliderCoords] = useState({
        left: 0,
        width: 0,
    });

    const setHoveredSliderCoordsCb = useCallback(
        (hoveredIndex: number) => {
            const hoveredTabRef = tabRefs[hoveredIndex];
            if (hoveredTabRef && hoveredTabRef.current) {
                const left = hoveredTabRef.current.offsetLeft;
                const width = hoveredTabRef.current.offsetWidth;
                setHoveredSliderCoords({ left, width });
            }
        },
        [tabRefs]
    );

    useEffect(() => {
        setHoveredSliderCoordsCb(selectedIndex);
    }, [selectedIndex, setHoveredSliderCoordsCb]);

    // Used to hide the hover slider for a tick to re-mount it back at the selected
    const [showHoverUnderline, setShowHoverUnderline] = useState(true);
    const moveHoverSliderBackToSelectedTab = (): void => {
        setShowHoverUnderline(false);
        setHoveredSliderCoordsCb(selectedIndex);
        setTimeout(() => {
            setShowHoverUnderline(true);
        }, 0);
    };

    const Underline = components.TabUnderline || DefaultTabUnderline;
    if (!Underline.displayName) {
        Underline.displayName = DefaultTabUnderline.displayName;
    }

    return (
        <TabsContainer
            {...tab}
            {...rest}
            ref={ref}
            // Note: onFocus bubbles in React, not in the DOM spec.
            // We don't care about focusing THIS element, but instead care about the
            // children receiving focus to start animations.
            onFocus={callAllHandlers(() => {
                showTransitions();
            }, rest.onFocus)}
            onMouseEnter={callAllHandlers(() => {
                showTransitions();
            }, rest.onMouseEnter)}
            onMouseLeave={callAllHandlers(() => {
                moveHoverSliderBackToSelectedTab();
            }, rest.onMouseLeave)}
        >
            {React.Children.map(children, (child, childIndex) => {
                if (!React.isValidElement(child)) {
                    return child;
                }

                const props = child.props as HTMLAttributes<HTMLElement>;

                return React.cloneElement(child, {
                    onMouseEnter: callAllHandlers(() => {
                        setHoveredSliderCoordsCb(childIndex);
                    }, props.onMouseEnter),

                    onClick: callAllHandlers(() => {
                        if (childIndex === selectedIndex) {
                            return;
                        }
                        showTransitions();
                    }, props.onClick),
                });
            })}
            {/* Full underline */}
            <Underline leftPx={0} widthPlusUnit={"100%"} backgroundColor={neutralLight20} />

            {/* Hover underline */}
            {showHoverUnderline && (
                <Underline
                    leftPx={hoveredSliderCoords.left}
                    widthPlusUnit={hoveredSliderCoords.width + "px"}
                    backgroundColor={neutralAccessible}
                    underlineTransition={underlineShouldTransitionRef.current}
                />
            )}

            {/* Selected underline */}
            <Underline
                leftPx={selectedSliderCoords.left}
                widthPlusUnit={selectedSliderCoords.width + "px"}
                backgroundColor={underlineTheme.selection(themeMode)}
                underlineTransition={underlineShouldTransitionRef.current}
            />
        </TabsContainer>
    );
});

TabList.displayName = "TabList";
